/**
 * Small geometry helpers for the SVG charts
 */

/**
 * Round axis ticks from 0 up to at least `max`, stepping by 1, 2 or 5 times a
 * power of ten (never below 1, since the values are counts),
 * e.g. 930 -> [0, 500, 1000], 3 -> [0, 1, 2, 3].
 * A non-positive max yields [0, 1] so an empty chart still has a scale.
 */
export function niceTicks(max: number, targetCount = 4): number[] {
  if (!(max > 0)) return [0, 1];

  const rawStep = max / Math.max(1, targetCount);
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const residual = rawStep / magnitude;
  const factor = residual <= 1 ? 1 : residual <= 2 ? 2 : residual <= 5 ? 5 : 10;
  const step = Math.max(1, factor * magnitude);
  const top = Math.ceil(max / step) * step;

  const ticks: number[] = [];
  for (let i = 0; i * step <= top; i++) {
    ticks.push(Number((i * step).toPrecision(12)));
  }
  return ticks;
}

/**
 * Evenly spaced indices (starting at 0) for at most `maxTicks` labels along
 * `count` positions
 */
export function pickTickIndices(count: number, maxTicks: number): number[] {
  if (count <= 0 || maxTicks <= 0) return [];
  const step = Math.ceil(count / maxTicks);
  const indices: number[] = [];
  for (let i = 0; i < count; i += step) {
    indices.push(i);
  }
  return indices;
}

/**
 * Indices of the buckets that start a new local day (for day-labelled time
 * axes), thinned evenly to at most `maxTicks`. The first bucket counts only
 * when it starts at midnight, so a partial first day gets no label crowding
 * the next one. Falls back to evenly spaced indices when no day starts.
 */
export function pickDayStartIndices(times: readonly string[], maxTicks: number): number[] {
  const dayStarts: number[] = [];
  let previousDay: string | null = null;
  times.forEach((time, index) => {
    const date = new Date(time);
    const day = date.toDateString();
    const startsDay = previousDay === null ? date.getHours() === 0 && date.getMinutes() === 0 : day !== previousDay;
    if (startsDay) dayStarts.push(index);
    previousDay = day;
  });

  if (dayStarts.length === 0) return pickTickIndices(times.length, maxTicks);
  const stride = Math.ceil(dayStarts.length / Math.max(1, maxTicks));
  return dayStarts.filter((_, position) => position % stride === 0);
}
